import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/root-store';
import { UploadedFile } from '@/shared/models/uploaded-file';

import { CarDocType, IModelOptions } from '../model';

const getVehiclesList = (state: RootState) => state.cars.models;
const getAllCars = (state: RootState) => state.cars.all;
const getCars = (state: RootState) => state.cars.cars;

const getCurrentCar = createSelector(
  getAllCars,
  (state: RootState) => state.cars.form.id,
  (cars, carId) => cars.find(c => c.id === carId),
);
const getCurrentUploads = createSelector(getCurrentCar, current => current?.docUploads);
const getCurrentCarDriver = createSelector(
  (state: RootState) => state.drivers.all,
  getCurrentCar,
  (drivers, car) => {
    const driverId = car?.currentChauffeur?.id;

    if (!driverId) {
      return undefined;
    }

    const fullDriver = drivers.find(driver => driver.id === driverId);

    return fullDriver ?? car.currentChauffeur;
  },
);

const getModelsByBrand = createSelector(
  (state: RootState) => state.cars.models,
  brands => {
    const modelsByBrand = new Map<string, Set<IModelOptions>>();

    brands.forEach(brand => {
      const make = brand.make;

      brand.models.forEach(model => {
        let models = modelsByBrand.get(make);

        if (!models) {
          models = new Set();
          modelsByBrand.set(make, models);
        }

        models.add(model);
      });
    });

    return modelsByBrand;
  },
);

const getCarForm = (state: RootState) => state.cars.form;

const getCurrentCarId = (state: RootState) => state.cars.form.id;

const getUploadedDocsForCurrentCar = createSelector(
  (state: RootState) => state.cars.form.id,
  (state: RootState) => state.cars.entities,

  (id, allCars): Partial<Record<CarDocType, UploadedFile<CarDocType>>> => {
    if (id !== undefined) {
      const car = allCars[id];

      if (car !== undefined) {
        return car.docUploads;
      }
    }

    return {};
  },
);

export {
  getVehiclesList,
  getModelsByBrand,
  getCarForm,
  getAllCars,
  getCars,
  getCurrentCarId,
  getUploadedDocsForCurrentCar,
  getCurrentCar,
  getCurrentUploads,
  getCurrentCarDriver,
};
